// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.57.0 Slice E — the processing-service certificate register
 * (work file §12, D43–D73; mount `/api/v1/service-certificates`).
 *
 * The organisation's record of a certificate it holds, its applications for
 * one, and what the authority wrote, as people in the organisation recorded
 * them. The register is a capability an installed framework DECLARES
 * (`service_certificate_declarations[]` on a framework record, read as
 * {@link ServiceCertificateDeclaration}); it is offered only to an
 * organisation that has recorded, on its profile, that it provides a service
 * the declaration's scope names (D51) — never prompted, inferred or counted
 * otherwise (AC-E7).
 *
 * ## What this file never holds (R-46)
 *
 * A certificate kind, window id, procedure id, part id, ground, form id,
 * authority, date or instrument. Those are pack content, read by id from the
 * installed declaration. The vocabularies below are the platform's: how a
 * record is kept (statuses, part states, document kinds), and the closed sets
 * a pack row's period is described with (owner, trigger, role).
 *
 * ## What the register never does
 *
 * It decides nothing for the authority and computes no consequence: an
 * evaluation that passes is not an issued certificate (D55), a refusal
 * computes nothing, a certificate has no expiry and no renewal (D60, AC-E3),
 * and no revocation risk is read from anything (D61).
 */
// ── Collections ─────────────────────────────────────────────────────
/** The certificates the organisation holds or held (D60). */
export const SERVICE_CERTIFICATES_COLLECTION = 'grc_service_certificates';
/** Applications for a certificate: issue or replacement (D55, D62). */
export const SERVICE_CERTIFICATE_APPLICATIONS_COLLECTION = 'grc_service_certificate_applications';
/** The authority's written communications, the Slice R shape (D57). */
export const SERVICE_CERTIFICATE_COMMUNICATIONS_COLLECTION = 'grc_service_certificate_communications';
/** Append-only history of every write to a certificate or an application (D60). */
export const SERVICE_CERTIFICATE_HISTORY_COLLECTION = 'grc_service_certificate_history';
// ── Platform vocabularies ───────────────────────────────────────────
/**
 * What an application asks for (D62). `replacement` covers re-issue and
 * exchange: a pack declares one or two replacement procedures per window,
 * each with its grounds.
 */
export const SERVICE_CERTIFICATE_PROCEDURE_KINDS = ['issue', 'replacement'];
/**
 * A certificate's status (D60). No `expired`, `not_applied` or `pending`: the
 * certificate states no end the platform reads, and an application is its own
 * record.
 */
export const SERVICE_CERTIFICATE_STATUSES = ['current', 'replaced', 'revoked'];
/** An application's status. `decided` covers both outcomes (D55). */
export const SERVICE_CERTIFICATE_APPLICATION_STATUSES = [
    'preparing',
    'filed',
    'decided',
    'withdrawn',
];
/**
 * The authority's decision on an application (D55): the words of
 * `FEE_APPLICATION_DECISIONS` (R-43), without its `pending` — an application
 * with no decision recorded has no decision.
 */
export const SERVICE_CERTIFICATE_DECISION_OUTCOMES = [
    'issued',
    'refused',
];
/**
 * A dossier part's state on an application (D59). `not_required_exception`
 * only where the part declares an `exception`, shown with the pack's label.
 */
export const SERVICE_CERTIFICATE_PART_STATES = [
    'provided',
    'not_provided',
    'not_required_exception',
];
/** What a document held on a certificate is (D60). */
export const SERVICE_CERTIFICATE_DOCUMENT_KINDS = [
    'certificate_paper',
    'certificate_electronic',
    'issue_decision',
    'revocation_decision',
    'dossier_as_filed',
    'other',
];
/**
 * What the authority sent (D57): the Slice R kinds
 * (`DOSSIER_AUTHORITY_COMMUNICATION_KINDS`) **minus `transfer_notice`** — the
 * certificate dossier is never transferred — **plus `decision`**, which a
 * decision on an application or a revocation arrives as.
 */
export const SERVICE_CERTIFICATE_COMMUNICATION_KINDS = [
    'receipt_acknowledgement',
    'result_notice',
    'supplement_request',
    'refusal',
    'other_written_communication',
    'decision',
];
const _sliceRKindsKept = true;
void _sliceRKindsKept;
/**
 * The receipt events an application records (D56, D58): the authority's
 * receipt of the application, and its receipt of the complete dossier after a
 * deficiency notice — never the first receipt by default. A subset of
 * `DOSSIER_RECEIPT_EVENTS`, so `stampFor` and `RecordReceiptModal` read them
 * unchanged.
 */
export const SERVICE_CERTIFICATE_RECEIPT_EVENTS = [
    'dossier_received',
    'complete_dossier_received',
];
/**
 * Whose period a pack period row is (D53): `obligation_owner` on the row.
 * `not_stated` — the text does not say whose period it is — is never dated;
 * the row's pack `owner_label` and `precaution` are shown as guidance.
 */
export const SERVICE_CERTIFICATE_PERIOD_OWNERS = [
    'authority',
    'organization',
    'not_stated',
];
/**
 * The evidenced event a period row counts from (D53): `period_trigger` on the
 * row. `not_stated` only with an owner of `not_stated`, and the reverse.
 */
export const SERVICE_CERTIFICATE_PERIOD_TRIGGERS = [
    'application_received',
    'complete_dossier_received',
    'replacement_request_received',
    'deficiency_notice_received',
    'revocation_decision_received',
    'not_stated',
];
/** A period row's role (D53), for ordering and the corpus pin only. */
export const SERVICE_CERTIFICATE_PERIOD_ROLES = [
    'evaluation',
    'decision',
    'deficiency_clause',
    'replacement_decision',
    'revoked_certificate_return',
];
/**
 * The installed deadline `event_type`s the register reads (D53): one row per
 * window × procedure × role, and the holder's duty to return a revoked
 * certificate, which is not windowed.
 */
export const SERVICE_CERTIFICATE_EVENT_TYPES = [
    'service_certificate_period',
    'service_certificate_holder_duty',
];
/** Which record a history entry is about (D60). */
export const SERVICE_CERTIFICATE_HISTORY_ENTITY_TYPES = ['certificate', 'application'];
/**
 * Why a period carries no date (D53, D54). Each is a different remedy, so none
 * is collapsed into a bare null — the `DossierAuthorityReviewAbsenceReason`
 * pattern.
 */
export const SERVICE_CERTIFICATE_PERIOD_ABSENCE_REASONS = [
    /** The row's owner is `not_stated`: never dated; its label and precaution are guidance. */
    'owner_not_stated',
    /** No receipt is evidenced, so no window and no row applies yet. */
    'governing_text_not_determined',
    /** The event the row counts from is not recorded (e.g. the complete-dossier receipt). */
    'trigger_not_evidenced',
    /** The row declares a period or unit the platform cannot count. */
    'uncomputable_period',
    /** A working-day row names no statutory calendar. */
    'statutory_calendar_not_declared',
    /** The named statutory calendar is not installed, reviewed or readable. */
    'statutory_calendar_unavailable',
    /** The installed calendar does not cover the year counted. */
    'statutory_calendar_not_covering',
];
/** A period as the application or certificate shows it (D53). */
export const SERVICE_CERTIFICATE_PERIOD_STATES = ['dated', 'passed', 'not_dated'];
/**
 * The organisation's recorded scope, as the register reads it (D47, D51). An
 * attribute nobody stated is `not_stated`; *unknown* is `declared_unknown`.
 *
 * `outside_declared_scope` (coordinator ruling (b), 2026-11-03, work file
 * §12.4.1): a valid selection of the attribute, none of whose values is among
 * those this declaration's `scope` lists. Treated exactly as not in scope —
 * no certificate or application is created (409
 * `SERVICE_CERTIFICATE_SCOPE_NOT_STATED`), and existing records stay readable
 * and recordable (D51).
 */
export const SERVICE_CERTIFICATE_SCOPE_STATES = [
    'in_scope',
    'not_stated',
    'stated_none',
    'declared_unknown',
    'outside_declared_scope',
];
// ── Error codes (§12.5.3) ───────────────────────────────────────────
/** The error codes the register's routes answer with, each a distinct case. */
export const SERVICE_CERTIFICATE_ERROR_CODES = {
    /** 404 — the certificate is not this organisation's or does not exist. */
    NOT_FOUND: 'SERVICE_CERTIFICATE_NOT_FOUND',
    /** 404 — the application is not this organisation's or does not exist. */
    APPLICATION_NOT_FOUND: 'SERVICE_CERTIFICATE_APPLICATION_NOT_FOUND',
    /** 409 — creating needs a recorded statement in scope. */
    SCOPE_NOT_STATED: 'SERVICE_CERTIFICATE_SCOPE_NOT_STATED',
    /** 409 — the register is not offered (export). */
    NOT_OFFERED: 'SERVICE_CERTIFICATE_NOT_OFFERED',
    /** 409 — `expected_updated_at` is not the record's. */
    RECORD_CHANGED: 'SERVICE_CERTIFICATE_RECORD_CHANGED',
    KIND_NOT_DECLARED: 'SERVICE_CERTIFICATE_KIND_NOT_DECLARED',
    SERVICE_NOT_DECLARED: 'SERVICE_CERTIFICATE_SERVICE_NOT_DECLARED',
    DATE_INVALID: 'SERVICE_CERTIFICATE_DATE_INVALID',
    OWNER_NOT_IN_ORGANISATION: 'SERVICE_CERTIFICATE_OWNER_NOT_IN_ORGANISATION',
    DOCUMENT_KIND_INVALID: 'SERVICE_CERTIFICATE_DOCUMENT_KIND_INVALID',
    ALREADY_REVOKED: 'SERVICE_CERTIFICATE_ALREADY_REVOKED',
    NOT_REVOKED: 'SERVICE_CERTIFICATE_NOT_REVOKED',
    GROUND_NOT_DECLARED: 'SERVICE_CERTIFICATE_GROUND_NOT_DECLARED',
    COMMUNICATION_INVALID: 'SERVICE_CERTIFICATE_COMMUNICATION_INVALID',
    /** Slice R's `DOSSIER_COMMUNICATION_RECEIPT_INVALID`, prefixed. */
    COMMUNICATION_RECEIPT_INVALID: 'SERVICE_CERTIFICATE_COMMUNICATION_RECEIPT_INVALID',
    /** Slice R's `DOSSIER_COMMUNICATION_NOT_FOUND`, prefixed. */
    COMMUNICATION_NOT_FOUND: 'SERVICE_CERTIFICATE_COMMUNICATION_NOT_FOUND',
    REPLACES_INVALID: 'SERVICE_CERTIFICATE_REPLACES_INVALID',
    APPLICATION_DECIDED: 'SERVICE_CERTIFICATE_APPLICATION_DECIDED',
    APPLICATION_NOT_PREPARING: 'SERVICE_CERTIFICATE_APPLICATION_NOT_PREPARING',
    APPLICATION_NOT_FILED: 'SERVICE_CERTIFICATE_APPLICATION_NOT_FILED',
    PART_NOT_DECLARED: 'SERVICE_CERTIFICATE_PART_NOT_DECLARED',
    PART_EXCEPTION_NOT_DECLARED: 'SERVICE_CERTIFICATE_PART_EXCEPTION_NOT_DECLARED',
    FORM_INSTANCE_INVALID: 'SERVICE_CERTIFICATE_FORM_INSTANCE_INVALID',
    CONDITION_LINK_NOT_DECLARED: 'SERVICE_CERTIFICATE_CONDITION_LINK_NOT_DECLARED',
    FILING_INVALID: 'SERVICE_CERTIFICATE_FILING_INVALID',
    CHANNEL_NOT_DECLARED: 'SERVICE_CERTIFICATE_CHANNEL_NOT_DECLARED',
    RECEIPT_ALREADY_RECORDED: 'SERVICE_CERTIFICATE_RECEIPT_ALREADY_RECORDED',
    RECEIPT_ALREADY_CORRECTED: 'SERVICE_CERTIFICATE_RECEIPT_ALREADY_CORRECTED',
    RECEIPT_DATE_INVALID: 'SERVICE_CERTIFICATE_RECEIPT_DATE_INVALID',
    SUPPLEMENT_REQUEST_NOT_FOUND: 'SERVICE_CERTIFICATE_SUPPLEMENT_REQUEST_NOT_FOUND',
    /** 409 — a replacement application has no evaluation. */
    EVALUATION_NOT_DECLARED: 'SERVICE_CERTIFICATE_EVALUATION_NOT_DECLARED',
    GOVERNING_TEXT_NOT_DETERMINED: 'SERVICE_CERTIFICATE_GOVERNING_TEXT_NOT_DETERMINED',
    EVALUATION_OPTION_NOT_OFFERED: 'SERVICE_CERTIFICATE_EVALUATION_OPTION_NOT_OFFERED',
    REPLACED_CERTIFICATE_NOT_CURRENT: 'SERVICE_CERTIFICATE_REPLACED_CERTIFICATE_NOT_CURRENT',
    DECISION_REASONS_REQUIRED: 'SERVICE_CERTIFICATE_DECISION_REASONS_REQUIRED',
    DECISION_CERTIFICATE_REQUIRED: 'SERVICE_CERTIFICATE_DECISION_CERTIFICATE_REQUIRED',
};
// ── The controller-side vendor certificate record (§12.13, D74–D86) ──
//
// Owner ruling P5-OQ3 (2026-11-03): a controller records, on a vendor that
// provides a listed service on its behalf, what that vendor states or shows
// about its certificate. A record of what someone said — never a verification,
// a duty of the controller, a gap, a score or a gate (D74, D75). Hosted on the
// governance vendor register (`/api/v1/governance/vendors/:vendorId/…`, D85),
// under `gov:vendor:read` / `gov:vendor:write` (D81).
/** Per vendor, per certificate kind: the services the vendor provides on the organisation's behalf (D78). Append-only revisions. */
export const VENDOR_PROCESSING_SERVICE_STATEMENTS_COLLECTION = 'grc_vendor_processing_service_statements';
/** Per vendor: what the vendor stated or showed about its certificate (D79). Append-only; a correction is a new entry. */
export const VENDOR_CERTIFICATE_ENTRIES_COLLECTION = 'grc_vendor_certificate_entries';
/**
 * A services statement's state (D78): `selected` (with 1–9 activities),
 * `none`, `unknown`. *Not recorded* is the absence of any revision, never a
 * state and never a gap.
 */
export const VENDOR_PROCESSING_SERVICE_STATEMENT_STATES = ['selected', 'none', 'unknown'];
/** The vendor's position on its certificate, as it stated it (D79). No derived status. */
export const VENDOR_CERTIFICATE_STATED_POSITIONS = [
    'holds',
    'applying',
    'does_not_hold',
    'declined_to_state',
];
/** Where the vendor's statement came from (D79). */
export const VENDOR_CERTIFICATE_STATEMENT_SOURCES = [
    'written_statement',
    'document_shown',
    'contract_clause',
    'other',
];
/** The vendor record's error codes (§12.13.4), each a distinct case. */
export const VENDOR_SERVICE_CERTIFICATE_ERROR_CODES = {
    /** 409 — no installed, operational declaration carries `vendor_check`. */
    CHECK_NOT_DECLARED: 'VENDOR_CERTIFICATE_CHECK_NOT_DECLARED',
    /** 409 — `expected_revision` is not the current one. */
    STATEMENT_REVISION_CONFLICT: 'VENDOR_PROCESSING_SERVICE_STATEMENT_REVISION_CONFLICT',
    /** 422 — an activity outside the declaration's service list. */
    SERVICE_NOT_DECLARED: 'VENDOR_PROCESSING_SERVICE_NOT_DECLARED',
    /** 422 — an evidence file the organisation does not hold for this vendor. */
    EVIDENCE_NOT_HELD: 'VENDOR_CERTIFICATE_EVIDENCE_NOT_HELD',
    /** 409 — a new entry while the current statement names no listed service (a correction is always allowed). */
    SERVICES_NOT_STATED: 'VENDOR_CERTIFICATE_SERVICES_NOT_STATED',
    /** 409 — the entry was already corrected. */
    ENTRY_ALREADY_CORRECTED: 'VENDOR_CERTIFICATE_ENTRY_ALREADY_CORRECTED',
    /** 422 — `holds` without a number or an issuing body as stated. */
    FIELDS_REQUIRED: 'VENDOR_CERTIFICATE_FIELDS_REQUIRED',
    /** 422 — a date after the organisation's local today, or an end before the issue. */
    DATE_INVALID: 'VENDOR_CERTIFICATE_DATE_INVALID',
    /** 422 — `correction_of` missing, on another vendor or another organisation. */
    CORRECTION_INVALID: 'VENDOR_CERTIFICATE_CORRECTION_INVALID',
};
//# sourceMappingURL=service-certificate.js.map